import { DutyRosterRecord, TripEndRecord } from "@/hooks/addins/useManualLogbookDutyRosters";

/** A journey with a roster number is at most this long from its confirmation to the end entry. */
export const JOURNEY_MAX_MS = 24 * 60 * 60 * 1000;

interface TripWindow {
  id: string;
  device: { id: string };
  start: Date;
  stop: Date;
}

export interface AnalogJourney<T extends TripWindow> {
  roster: DutyRosterRecord;
  tripEnd?: TripEndRecord;
  /** Geotab trips that started inside the journey, in time order. */
  trips: T[];
  /** Roster confirmations behind this journey: 1, or more when the driver confirmed repeatedly. */
  candidates: number;
}

/** The driver's odometer entry as a number; undefined when left blank or not a positive number. */
export function readingKm(record: { odometerKm: string } | undefined): number | undefined {
  if (!record?.odometerKm) return undefined;
  const value = Number(record.odometerKm);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

const byTime = (a: { recordedAt: Date }, b: { recordedAt: Date }) => a.recordedAt.getTime() - b.recordedAt.getTime();

/**
 * Driving time of an analog journey: the Geotab trips between the roster confirmation and the
 * driver's end entry added up; when Geotab recorded none, the time between the two entries.
 * Undefined while the end entry is missing, like every other end figure of the journey.
 */
export function analogDurationMs<T extends TripWindow>(
  journey: Pick<AnalogJourney<T>, "roster" | "tripEnd" | "trips">,
): { durationMs: number; fromGeotabTrips: boolean } | undefined {
  if (!journey.tripEnd) return undefined;
  if (journey.trips.length === 0) {
    return { durationMs: Math.max(0, journey.tripEnd.recordedAt.getTime() - journey.roster.recordedAt.getTime()), fromGeotabTrips: false };
  }
  const durationMs = journey.trips.reduce((sum, trip) => sum + Math.max(0, trip.stop.getTime() - trip.start.getTime()), 0);
  return { durationMs, fromGeotabTrips: true };
}

/**
 * Builds the analog journeys of one device. Every duty roster record with a number opens a
 * journey at its `recordedAt`. The journey closes with the first end-of-trip record that carries
 * the same roster number (or, when the app was restarted in between, the first one without a
 * number), at the latest with the driver's next confirmation or after 24 hours. Geotab trips that
 * start inside the journey belong to it and are not digital entries. A confirmation that neither
 * has a trip nor an end record before the next confirmation is a repeat: it is folded into the
 * next journey and only counted as a candidate.
 */
export function buildAnalogJourneys<T extends TripWindow>(
  trips: T[],
  rosters: DutyRosterRecord[],
  tripEnds: TripEndRecord[],
): AnalogJourney<T>[] {
  const sortedRosters = rosters.filter(record => record.dutyRosterNumber.trim() !== "").sort(byTime);
  const sortedTrips = [...trips].sort((a, b) => a.start.getTime() - b.start.getTime());
  const sortedEnds = [...tripEnds].sort(byTime);
  const usedEnds = new Set<string>();
  const journeys: AnalogJourney<T>[] = [];
  let repeats = 0;

  sortedRosters.forEach((roster, index) => {
    const startMs = roster.recordedAt.getTime();
    const nextMs = sortedRosters[index + 1]?.recordedAt.getTime() ?? Number.POSITIVE_INFINITY;
    const limitMs = Math.min(nextMs, startMs + JOURNEY_MAX_MS);
    const findEnd = (number: string) =>
      sortedEnds.find(record => {
        const time = record.recordedAt.getTime();
        return !usedEnds.has(record.id) && record.dutyRosterNumber === number && time >= startMs && time < limitMs;
      });
    const tripEnd = findEnd(roster.dutyRosterNumber) ?? findEnd("");
    const coverEndMs = tripEnd ? tripEnd.recordedAt.getTime() : limitMs;
    const covered = sortedTrips.filter(trip => {
      const start = trip.start.getTime();
      return start >= startMs && start < coverEndMs;
    });
    if (covered.length === 0 && !tripEnd) {
      repeats++;
      return;
    }
    if (tripEnd) usedEnds.add(tripEnd.id);
    journeys.push({ roster, tripEnd, trips: covered, candidates: repeats + 1 });
    repeats = 0;
  });
  return journeys;
}
